import { LoadingSpaceType } from 'src/app/core/services/import-export-service-types';
import { Project } from 'src/app/core/objects/project';
import {
  CargoSpace,
  Dimensions,
  Group,
  LoadsCube,
  LoadsState,
  PalletInterlayer,
  PalletLayer,
  PalletLayerGroup,
  Position,
  WorkSpace,
} from 'src/app/core/types';
import { layerArrangementSignature } from './pallet-layer-add.helper';

/** Cargo spaces currently shown on the 3D visualization for the given workspace/stop. */
export function getVisualizationCargoSpaces(
  project: Project | undefined | null,
  workSpaceIndex: number | undefined,
  stopIndex: number | undefined,
): CargoSpace[] {
  if (!project?.workSpaces?.length) {
    return [];
  }
  const wsIndex = workSpaceIndex ?? 0;
  const workSpace = project.workSpaces[wsIndex];
  if (!workSpace) {
    return [];
  }
  if (stopIndex !== undefined && stopIndex !== null && stopIndex >= 0 && workSpace.stops?.[stopIndex]) {
    return workSpace.stops[stopIndex].cargoSpaces ?? [];
  }
  return workSpace.cargoSpaces ?? [];
}

/** True when the active workspace/stop uses a pallet loading space (type 3), matching the 3D view. */
export function isActivePalletLoadingSpace(
  project: Project | undefined | null,
  workSpaceIndex: number | undefined,
  stopIndex: number | undefined,
): boolean {
  return getVisualizationCargoSpaces(project, workSpaceIndex, stopIndex).some(
    (cargoSpace) => cargoSpace?.type === LoadingSpaceType.Pallet,
  );
}

export function getCargoSpaceAt(
  project: Project | undefined | null,
  workSpaceIndex: number,
  cargoSpaceIndex: number,
  stopIndex?: number,
): CargoSpace | undefined {
  if (!project?.workSpaces?.length) {
    return undefined;
  }
  const workSpace = project.workSpaces[workSpaceIndex ?? 0];
  if (!workSpace) {
    return undefined;
  }
  if (stopIndex !== undefined && stopIndex !== null && stopIndex >= 0 && workSpace.stops?.[stopIndex]) {
    return workSpace.stops[stopIndex].cargoSpaces?.[cargoSpaceIndex];
  }
  return workSpace.cargoSpaces?.[cargoSpaceIndex];
}

export function getPalletLayerGroupsForList(
  project: Project | undefined | null,
  workSpaceIndex: number,
  cargoSpaceIndex: number,
  stopIndex?: number,
): PalletLayerGroup[] {
  return getPalletLayerGroupsFromCargoSpace(
    getCargoSpaceAt(project, workSpaceIndex, cargoSpaceIndex, stopIndex),
  );
}

/** Normalize API field names for pallet layer groups on a cargo space. */
export function getPalletLayerGroupsFromCargoSpace(
  cargoSpace: CargoSpace | undefined,
): PalletLayerGroup[] {
  if (!cargoSpace) {
    return [];
  }
  const raw = cargoSpace as CargoSpace & Record<string, unknown>;
  const groups =
    cargoSpace.palletLayersGroups ??
    cargoSpace.palletLayerGroups ??
    raw.palletLayersGroups ??
    raw.palletLayerGroups ??
    [];
  return Array.isArray(groups) ? groups : [];
}

/** True when this cargo group is already shown via palletLayersGroups in the left list. */
export function isGroupRepresentedInPalletLayers(
  group: Group,
  cargoSpace: CargoSpace | undefined,
): boolean {
  const palletGroups = getPalletLayerGroupsFromCargoSpace(cargoSpace);
  if (!palletGroups.length) {
    return false;
  }
  const groupLoadType = group.loadType ?? 0;
  return palletGroups.some((plg) => {
    if (plg.id === group.id) {
      return true;
    }
    if (plg.color === group.color && (plg.loadType ?? 0) === groupLoadType) {
      return true;
    }
    return (plg.layers ?? []).some((layer) => layer.id === group.id);
  });
}

export function listGroupsExcludingPalletLayers(
  cargoSpace: CargoSpace | undefined,
): Group[] {
  if (
    cargoSpace?.type === LoadingSpaceType.Pallet &&
    cargoSpaceHasLoadedPalletLayers(cargoSpace)
  ) {
    return [];
  }
  const groups = cargoSpace?.groups ?? [];
  // Return the same array reference when nothing is filtered out so CDK
  // transferArrayItem/moveItemInArray mutates project state (needed for group/drag payload).
  const needsFilter = groups.some(
    (g) => !hasNonZeroQuantity(g) || isGroupRepresentedInPalletLayers(g, cargoSpace),
  );
  if (!needsFilter) {
    return groups;
  }
  return groups.filter(
    (g) => hasNonZeroQuantity(g) && !isGroupRepresentedInPalletLayers(g, cargoSpace),
  );
}

/** True when pallet layer group should appear in summaries (has quantity or loaded stacks). */
export function palletLayerGroupHasLoadedCargo(palletGroup: PalletLayerGroup): boolean {
  if ((palletGroup.quantity ?? 0) > 0) {
    return true;
  }
  return filterLoadedLayerTypes(palletGroup.layers ?? []).length > 0;
}

function findMatchingCargoGroupForPalletLayerGroup(
  cargoSpace: CargoSpace,
  palletGroup: PalletLayerGroup,
): Group | undefined {
  return (cargoSpace.groups ?? []).find(
    (group) =>
      group.id === palletGroup.id ||
      (group.color === palletGroup.color && group.loadType === palletGroup.loadType),
  );
}

/** Pallet layer group as a standard load row (no per-layer breakdown). */
export function palletLayerGroupToSummaryGroup(
  palletGroup: PalletLayerGroup,
  cargoSpace?: CargoSpace,
): Group {
  const matchingGroup = cargoSpace
    ? findMatchingCargoGroupForPalletLayerGroup(cargoSpace, palletGroup)
    : undefined;
  return {
    id: palletGroup.id,
    loadType: palletGroup.loadType,
    color: palletGroup.color,
    name: palletGroup.name ?? matchingGroup?.name ?? '',
    dimensions: palletGroup.dimensions ??
      matchingGroup?.dimensions ?? { length: 0, width: 0, height: 0 },
    quantity: palletGroup.quantity ?? matchingGroup?.quantity ?? 0,
    weight: palletGroup.weight ?? matchingGroup?.weight ?? 0,
    destination: matchingGroup?.destination,
    createdAt: palletGroup.createdAt ?? matchingGroup?.createdAt ?? 0,
    stacking: matchingGroup?.stacking ?? false,
    loadsCubes: [],
    centerOfMass: matchingGroup?.centerOfMass ?? { x: 0, y: 0, z: 0 },
    allowToRotate: matchingGroup?.allowToRotate ?? false,
    rotateFreely: matchingGroup?.rotateFreely ?? false,
    rotateOnBase: matchingGroup?.rotateOnBase ?? false,
    allowOutOfSpace: matchingGroup?.allowOutOfSpace ?? false,
  };
}

export function getPalletLayerGroupsForPdfSummary(cargoSpace: CargoSpace | undefined): Group[] {
  if (!cargoSpace) {
    return [];
  }
  return getPalletLayerGroupsFromCargoSpace(cargoSpace)
    .filter(palletLayerGroupHasLoadedCargo)
    .map((palletGroup) => palletLayerGroupToSummaryGroup(palletGroup, cargoSpace));
}

/** Load groups for PDF tables: individual loads plus pallet summaries (without layer breakdown). */
export function getPdfCargoSpaceLoadGroups(cargoSpace: CargoSpace | undefined): Group[] {
  return [
    ...listGroupsExcludingPalletLayers(cargoSpace),
    ...getPalletLayerGroupsForPdfSummary(cargoSpace),
  ];
}

/** Groups eligible for PDF loading-order steps (excludes pallet layer stacks). */
export function getPdfLoadingStepGroups(cargoSpace: CargoSpace | undefined): Group[] {
  return listGroupsExcludingPalletLayers(cargoSpace);
}

export function visualizationGroupsForCargoSpace(cargoSpace: CargoSpace | undefined): Group[] {
  const palletDeckY = getPalletDeckOffsetY(cargoSpace);
  const regularGroups = liftRegularGroupsOntoPalletDeck(
    listGroupsExcludingPalletLayers(cargoSpace),
    cargoSpace,
    palletDeckY,
  );
  const palletFootprint = getPalletFootprint(cargoSpace);
  let layerGroupYOffset = 0;
  const layerGroups = getPalletLayerGroupsFromCargoSpace(cargoSpace)
    .filter(shouldVisualizePalletLayerGroup)
    .flatMap((palletGroup) => {
      const { groups, stackHeight } = palletLayerGroupToVisualizationGroups(
        palletGroup,
        layerGroupYOffset,
        palletFootprint,
        palletDeckY,
        cargoSpace,
      );
      layerGroupYOffset = stackHeight;
      return groups;
    })
    .filter((group) => group.loadsCubes.length > 0);

  return nestedPalletLoadsOnSpace(cargoSpace).concat(regularGroups).concat(layerGroups);
}

function nestedPalletLoadsOnSpace(cargoSpace: CargoSpace | undefined): Group[] {
  if (cargoSpace?.type !== LoadingSpaceType.Pallet) {
    return [];
  }
  if (!cargoSpaceHasLoadedPalletLayers(cargoSpace)) {
    return [];
  }
  const extras = (cargoSpace.groups ?? []).filter(
    (group) =>
      hasNonZeroQuantity(group) &&
      !isGroupRepresentedInPalletLayers(group, cargoSpace) &&
      isNestedPalletCargo(group) &&
      (group.loadsCubes?.length ?? 0) > 0,
  );
  return liftRegularGroupsOntoPalletDeck(
    extras,
    cargoSpace,
    getPalletDeckOffsetY(cargoSpace),
  );
}

function isNestedPalletCargo(group: Group): boolean {
  if ((group.loadType ?? 0) === 3) {
    return true;
  }
  const dimensions = group.loadsCubes?.[0]?.dimensions ?? group.dimensions;
  const width = dimensions?.width ?? 0;
  const length = dimensions?.length ?? 0;
  return width >= 60 && length >= 60;
}

export const PALLET_CONTENTS_COOKIE = 'palletContents';
const PALLET_LOAD_TYPE = 3;
const PALLET_DECK_COLOR = 0xc2a36b;

export function isPalletContentsViewEnabled(
  cookieGet?: (name: string) => string,
): boolean {
  return cookieGet?.(PALLET_CONTENTS_COOKIE)?.includes('true') === true;
}

export function scaleVisualizationGroupForScene(group: Group, factor = 100): Group {
  const scaleDim = (dimensions?: Dimensions): Dimensions | undefined => {
    if (!dimensions) {
      return dimensions;
    }
    return {
      ...dimensions,
      width: roundSceneScale((dimensions.width ?? 0) * factor),
      length: roundSceneScale((dimensions.length ?? 0) * factor),
      height: roundSceneScale((dimensions.height ?? 0) * factor),
      diameter: roundSceneScale((dimensions.diameter ?? 0) * factor),
    };
  };

  return {
    ...group,
    dimensions: scaleDim(group.dimensions) ?? group.dimensions,
    loadsCubes: (group.loadsCubes ?? []).map((cube) => ({
      ...cube,
      position: {
        x: roundSceneScale((cube.position?.x ?? 0) * factor),
        y: roundSceneScale((cube.position?.y ?? 0) * factor),
        z: roundSceneScale((cube.position?.z ?? 0) * factor),
      },
      dimensions: scaleDim(cube.dimensions),
    })),
  };
}

function roundSceneScale(value: number): number {
  return Math.round(value * 100) / 100;
}

export function expandLinkedPalletContentsForVehicle(
  vehicleCargoSpace: CargoSpace | undefined,
  allProjectCargoSpaces: CargoSpace[] = [],
  showPalletContents = false,
): Group[] {
  const groups = visualizationGroupsForCargoSpace(vehicleCargoSpace);
  if (
    !showPalletContents ||
    !vehicleCargoSpace ||
    vehicleCargoSpace.type === LoadingSpaceType.Pallet
  ) {
    return groups;
  }

  const remainingPallets = allProjectCargoSpaces.filter((cargoSpace) =>
    isLinkedPalletForVehicle(cargoSpace, vehicleCargoSpace.id),
  );
  const vehicleGroupIds = new Set(
    (vehicleCargoSpace.groups ?? [])
      .map((group) => Number(group.id))
      .filter((id) => !Number.isNaN(id)),
  );
  const palletGroupCount = groups.filter((group) => (group.loadType ?? 0) === PALLET_LOAD_TYPE).length;

  return groups.map((group) => {
    if ((group.loadType ?? 0) !== PALLET_LOAD_TYPE) {
      return group;
    }
    const sourcePallet =
      takeMatchingSourcePallet(group, remainingPallets) ??
      takeStaleLinkedPallet(group, remainingPallets, vehicleGroupIds, palletGroupCount);
    if (!sourcePallet) {
      return group;
    }
    const nativeContents = buildPalletContentOverlayGroups(sourcePallet);
    if (!nativeContents.length) {
      return group;
    }
    const loadsCubes = (group.loadsCubes ?? []).map((cube) => ({
      ...cube,
      palletContentGroups: contentsForPlacedPalletCube(nativeContents, sourcePallet, cube, group),
    }));
    return {
      ...group,
      loadsCubes,
      palletContentGroups: loadsCubes[0]?.palletContentGroups ?? nativeContents,
    };
  });
}

function contentsForPlacedPalletCube(
  nativeContents: Group[],
  sourcePallet: CargoSpace,
  cube: LoadsCube,
  group: Group,
): Group[] {
  if ((cube.yRotation ?? 0) !== 0 || (cube.edgeRotation ?? 0) !== 0) {
    return nativeContents;
  }
  return remapPalletContentsToVehicleFootprint(
    nativeContents,
    sourcePallet,
    placedPalletFootprint(cube, group),
  );
}

function placedPalletFootprint(cube: LoadsCube, group: Group): Dimensions | undefined {
  const dims =
    cube.dimensions?.width && cube.dimensions?.length ? cube.dimensions : group.dimensions;
  if (!dims?.width || !dims.length) {
    return undefined;
  }
  return dims;
}

function isLinkedPalletForVehicle(cargoSpace: CargoSpace, vehicleId: number): boolean {
  if (cargoSpace.type !== LoadingSpaceType.Pallet) {
    return false;
  }
  const features = cargoSpace.extraFeatures;
  if (!features || features.palletVehicle == null) {
    return false;
  }
  // Deleting the outside pallet clears palletVehicleToLoad and palletIdInVehicle.
  // palletVehicle still points at this cargo space.
  if (features.palletVehicleToLoad === false) {
    return false;
  }
  return Number(features.palletVehicle) === Number(vehicleId);
}

function sameGroupId(left: unknown, right: unknown): boolean {
  if (left == null || right == null) {
    return false;
  }
  const leftId = Number(left);
  const rightId = Number(right);
  return !Number.isNaN(leftId) && leftId === rightId;
}

function takeMatchingSourcePallet(
  group: Group,
  remainingPallets: CargoSpace[],
): CargoSpace | undefined {
  const index = remainingPallets.findIndex((pallet) =>
    sameGroupId(pallet.extraFeatures?.palletIdInVehicle, group.id),
  );
  if (index < 0) {
    return undefined;
  }
  return remainingPallets.splice(index, 1)[0];
}

/**
 * Deleting the outside pallet can drop palletIdInVehicle or leave it pointing at that
 * removed group. The pallet space is still loaded onto this vehicle, so keep its contents.
 */
function takeStaleLinkedPallet(
  group: Group,
  remainingPallets: CargoSpace[],
  vehicleGroupIds: Set<number>,
  palletGroupCount: number,
): CargoSpace | undefined {
  const unmatched = remainingPallets.filter((pallet) => {
    const linkedId = pallet.extraFeatures?.palletIdInVehicle;
    if (linkedId == null || Number.isNaN(Number(linkedId))) {
      return true;
    }
    return !vehicleGroupIds.has(Number(linkedId));
  });
  const identified = unmatched.find(
    (pallet) => palletFootprintMatchesGroup(pallet, group) || palletNameMatchesGroup(pallet, group),
  );
  const chosen =
    identified ?? (unmatched.length === 1 && palletGroupCount === 1 ? unmatched[0] : undefined);
  if (!chosen) {
    return undefined;
  }
  const index = remainingPallets.indexOf(chosen);
  return index < 0 ? undefined : remainingPallets.splice(index, 1)[0];
}

function palletNameMatchesGroup(pallet: CargoSpace, group: Group): boolean {
  const palletName = pallet.name?.trim();
  const groupName = group.name?.trim();
  return !!palletName && palletName === groupName;
}

function palletFootprintMatchesGroup(pallet: CargoSpace, group: Group): boolean {
  const footprint = getPalletFootprint(pallet);
  const dims = group.dimensions;
  if (!footprint?.width || !footprint.length || !dims?.width || !dims.length) {
    return false;
  }
  const same = (left: number, right: number) => Math.abs(left - right) < 0.01;
  const aligned = same(footprint.width, dims.width) && same(footprint.length, dims.length);
  const swapped = same(footprint.width, dims.length) && same(footprint.length, dims.width);
  return aligned || swapped;
}

function buildPalletContentOverlayGroups(sourcePallet: CargoSpace): Group[] {
  const overlayGroups = visualizationGroupsForCargoSpace(sourcePallet);
  if (!overlayGroups.length) {
    return [];
  }
  const deck = buildPalletDeckOverlayGroup(sourcePallet);
  return deck ? [deck, ...overlayGroups] : overlayGroups;
}

function isSwappedFootprint(source: Dimensions, vehicle: Dimensions): boolean {
  const same = (a: number, b: number) => Math.abs((a ?? 0) - (b ?? 0)) < 0.01;
  if (same(source.width, source.length)) {
    return false;
  }
  const aligned = same(source.width, vehicle.width) && same(source.length, vehicle.length);
  const swapped = same(source.width, vehicle.length) && same(source.length, vehicle.width);
  return swapped && !aligned;
}

function rotateCube90CcwAroundPalletOrigin(
  cube: LoadsCube,
  sourceWidth: number,
  fallbackDimensions?: Dimensions,
): LoadsCube {
  const dims = cube.dimensions ?? fallbackDimensions;
  const width = dims?.width ?? 0;
  const length = dims?.length ?? 0;
  const x = cube.position?.x ?? 0;
  const z = cube.position?.z ?? 0;
  return {
    ...cube,
    position: {
      ...cube.position,
      x: z,
      z: sourceWidth - x - width,
    },
    dimensions: dims ? { ...dims, width: length, length: width } : cube.dimensions,
  };
}

function remapPalletContentsToVehicleFootprint(
  contentGroups: Group[],
  sourcePallet: CargoSpace,
  vehicle: Dimensions | undefined,
): Group[] {
  const source = getPalletFootprint(sourcePallet);
  if (!source?.width || !source.length || !vehicle || !isSwappedFootprint(source, vehicle)) {
    return contentGroups;
  }
  const sourceWidth = source.width;
  return contentGroups.map((group) => {
    const dims = group.dimensions;
    const swapGroupDims =
      !!dims &&
      Math.abs((dims.width ?? 0) - source.width) < 0.01 &&
      Math.abs((dims.length ?? 0) - source.length) < 0.01;
    return {
      ...group,
      dimensions: swapGroupDims
        ? { ...dims, width: source.length, length: source.width }
        : dims,
      loadsCubes: (group.loadsCubes ?? []).map((cube) =>
        rotateCube90CcwAroundPalletOrigin(cube, sourceWidth, group.dimensions),
      ),
    };
  });
}

function buildPalletDeckOverlayGroup(sourcePallet: CargoSpace): Group | undefined {
  const height = getPalletDeckOffsetY(sourcePallet);
  const footprint = getPalletFootprint(sourcePallet);
  if (!(height > 0) || !footprint?.width || !footprint.length) {
    return undefined;
  }
  const dimensions: Dimensions = {
    width: footprint.width,
    length: footprint.length,
    height,
  };
  return {
    id: -2,
    loadType: 0,
    color: PALLET_DECK_COLOR,
    name: sourcePallet.name ?? '',
    dimensions,
    quantity: 1,
    weight: sourcePallet.extraFeatures?.palletWeight ?? 0,
    createdAt: 0,
    stacking: false,
    loadsCubes: [
      {
        position: { x: 0, y: 0, z: 0 },
        dimensions,
        loadsPerAxis: { x: 1, y: 1, z: 1 },
        state: { rotated: false, flipped: false, dragged: false },
      },
    ],
    stackingOptions: { palletDeck: true },
    centerOfMass: { x: 0, y: 0, z: 0 },
    allowToRotate: false,
    rotateFreely: false,
    rotateOnBase: false,
    allowOutOfSpace: false,
  };
}

/**
 * Optimizer sometimes places regular (non-layer) loads at y=0 on pallet spaces.
 * Lift whole groups whose floor sits below the deck so cargo does not sink into the base.
 */
function liftRegularGroupsOntoPalletDeck(
  groups: Group[],
  cargoSpace: CargoSpace | undefined,
  palletDeckY: number,
): Group[] {
  if (cargoSpace?.type !== LoadingSpaceType.Pallet || !(palletDeckY > 0)) {
    return groups;
  }

  return groups.map((group) => {
    const cubes = group.loadsCubes ?? [];
    if (!cubes.length) {
      return group;
    }
    const minY = Math.min(...cubes.map((cube) => cube.position?.y ?? 0));
    if (minY >= palletDeckY - 0.01) {
      return group;
    }
    const offsetY = palletDeckY - minY;
    return {
      ...group,
      loadsCubes: cubes.map((cube) => ({
        ...cube,
        position: {
          ...cube.position,
          y: (cube.position?.y ?? 0) + offsetY,
        },
      })),
    };
  });
}

/** Physical pallet base footprint (width/length) used to size interlayer slabs. */
function getPalletFootprint(cargoSpace: CargoSpace | undefined): Dimensions | undefined {
  const dims = cargoSpace?.dimensions;
  if (!dims?.width || !dims.length) {
    return undefined;
  }
  return { width: dims.width, length: dims.length, height: dims.height };
}

function hasNonZeroQuantity(group: { quantity?: number }): boolean {
  return group.quantity !== 0;
}

function shouldVisualizePalletLayerGroup(palletGroup: PalletLayerGroup): boolean {
  return palletLayerGroupHasLoadedCargo(palletGroup);
}

function buildVisualizationGroup(
  palletGroup: PalletLayerGroup,
  loadsCubes: LoadsCube[],
  overrides?: Partial<Pick<Group, 'color' | 'name' | 'nameTranslated' | 'weight'>>,
  cargoSpace?: CargoSpace,
): Group {
  const matchingGroup = cargoSpace
    ? findMatchingCargoGroupForPalletLayerGroup(cargoSpace, palletGroup)
    : undefined;
  const name = overrides?.name ?? matchingGroup?.name ?? palletGroup.name ?? '';
  const nameTranslated =
    overrides?.nameTranslated ??
    matchingGroup?.nameTranslated ??
    palletGroup.nameTranslated ??
    matchingGroup?.name ??
    palletGroup.name ??
    name;
  const showLoadName =
    matchingGroup?.showLoadName ?? palletGroup.showLoadName ?? false;
  const firstCube = loadsCubes[0];
  // Interlayer slabs must keep their own thickness; resolveIndividualLoadDimensions
  // would inflate height up to the catalog load height and break volume summaries.
  const dimensions = firstCube?.stackingOptions?.interlayer
    ? (firstCube.dimensions ??
      palletGroup.dimensions ?? {
        width: 0,
        height: 0,
        length: 0,
      })
    : (resolveIndividualLoadDimensions(firstCube, palletGroup) ??
      palletGroup.dimensions ?? {
        width: 0,
        height: 0,
        length: 0,
      });

  return {
    id: palletGroup.id,
    loadType: palletGroup.loadType,
    color: overrides?.color ?? palletGroup.color,
    name,
    dimensions,
    quantity: loadsCubes.length,
    weight: overrides?.weight ?? palletGroup.weight ?? 0,
    createdAt: palletGroup.createdAt ?? 0,
    stacking: false,
    loadsCubes,
    stackingOptions: palletGroup.stackingOptions,
    nameTranslated,
    showLoadName,
    centerOfMass: { x: 0, y: 0, z: 0 },
    allowToRotate: false,
    rotateFreely: false,
    rotateOnBase: false,
    allowOutOfSpace: false,
  };
}

function palletLayerGroupToVisualizationGroups(
  palletGroup: PalletLayerGroup,
  baseYOffset = 0,
  palletFootprint?: Dimensions,
  palletDeckY = 0,
  cargoSpace?: CargoSpace,
): { groups: Group[]; stackHeight: number } {
  const { layerCubes, interlayerCubes, interlayer, stackHeight } = expandPalletStackCubes(
    palletGroup,
    baseYOffset,
    palletFootprint,
    palletDeckY,
  );
  const groups: Group[] = [];

  if (layerCubes.length) {
    groups.push(buildVisualizationGroup(palletGroup, layerCubes, undefined, cargoSpace));
  }
  if (interlayerCubes.length && interlayer) {
    groups.push({
      ...buildVisualizationGroup(
        palletGroup,
        interlayerCubes,
        {
          color: interlayer.color,
          name: interlayer.name,
          nameTranslated: interlayer.name,
          weight: interlayer.weight ?? 0,
        },
        cargoSpace,
      ),
      stackingOptions: { maxLoadsOnTop: 0, interlayer: true },
    });
  }

  return { groups, stackHeight };
}

function createInterlayerLoadCube(
  interlayer: PalletInterlayer,
  position: Position,
  dimensions: Dimensions,
): LoadsCube {
  const emptyState: LoadsState = { rotated: false, flipped: false, dragged: false };

  return {
    position: {
      x: position.x ?? 0,
      y: position.y ?? 0,
      z: position.z ?? 0,
    },
    dimensions,
    color: interlayer.color,
    loadsPerAxis: { x: 1, y: 1, z: 1 },
    yRotation: 0,
    edgeRotation: 0,
    state: emptyState,
    stackingOptions: { maxLoadsOnTop: 0, interlayer: true },
  };
}

function getNormalizedInterlayerForStack(
  palletGroup: PalletLayerGroup,
  palletFootprint?: Dimensions,
): PalletInterlayer | undefined {
  if (!palletGroup.interlayer) {
    return undefined;
  }
  const layerTemplate = findFirstLayerLoadTemplate(palletGroup);
  return normalizePalletInterlayerForVisualization(
    palletGroup.interlayer,
    palletGroup,
    layerTemplate?.dimensions ?? palletGroup.dimensions,
    palletFootprint,
  );
}

function getInterlayerSlabDimensions(interlayer: PalletInterlayer): Dimensions | undefined {
  const height = interlayer.dimensions?.height ?? 0;
  const width = interlayer.dimensions?.width ?? 0;
  const length = interlayer.dimensions?.length ?? 0;
  if (height <= 0 || width <= 0 || length <= 0) {
    return undefined;
  }
  return { width, length, height };
}

function expandInterlayerCubesFromApi(
  interlayer: PalletInterlayer,
  slab: Dimensions,
  placedInterlayers: PalletInterlayer['loadedInterlayers'],
): LoadsCube[] {
  return (placedInterlayers ?? []).map((placed) =>
    createInterlayerLoadCube(
      interlayer,
      {
        x: placed.position?.x ?? 0,
        y: placed.position?.y ?? 0,
        z: placed.position?.z ?? 0,
      },
      slab,
    ),
  );
}

/** Tiny Y gap so load faces and interlayer slabs do not z-fight in the canvas. */
export const VISUAL_STACK_CONTACT_GAP = 0.15;

/** Layer templates may store slab height; use catalog load size for 3D boxes and labels. */
export function resolveIndividualLoadDimensions(
  loadCube: LoadsCube | undefined,
  palletGroup: PalletLayerGroup,
): Dimensions {
  const catalog = palletGroup.dimensions;
  const cube = loadCube?.dimensions;
  if (!cube) {
    return catalog ?? { width: 0, height: 0, length: 0 };
  }
  const catalogHeight = getVisualLoadStackHeight(palletGroup.loadType, catalog, catalog);
  const cubeHeight = getVisualLoadStackHeight(palletGroup.loadType, cube, catalog);
  const height =
    catalogHeight > 0 && cubeHeight > 0 && cubeHeight < catalogHeight
      ? catalogHeight
      : cubeHeight;
  return {
    width: cube.width ?? catalog?.width ?? 0,
    length: cube.length ?? catalog?.length ?? 0,
    height,
    diameter: cube.diameter ?? catalog?.diameter,
  };
}

function normalizePalletLayerLoadCubeForVisualization(
  loadCube: LoadsCube,
  y: number,
  dimensions?: Dimensions,
): LoadsCube {
  return {
    ...loadCube,
    position: {
      ...loadCube.position,
      y,
    },
    dimensions: dimensions ?? loadCube.dimensions,
    loadsPerAxis: { x: 1, y: 1, z: 1 },
  };
}

interface PlacedLayerStack {
  position?: Position;
  numberOfStackingLayers?: number;
}

function getPlacedLayerStacks(layer: PalletLayer): PlacedLayerStack[] {
  if (!Array.isArray(layer.layersLoaded)) {
    return [];
  }
  return layer.layersLoaded.filter((entry) => {
    const placed = entry as PlacedLayerStack;
    return !!placed.position && (placed.numberOfStackingLayers ?? 0) > 0;
  }) as PlacedLayerStack[];
}

function dedupePlacedStacksByFloorY(stacks: PlacedLayerStack[]): PlacedLayerStack[] {
  const seen = new Set<number>();
  return stacks.filter((stack) => {
    const y = stack.position?.y ?? 0;
    if (seen.has(y)) {
      return false;
    }
    seen.add(y);
    return true;
  });
}

function sanitizeLoadsPerAxisForVisualization(loadCube: LoadsCube): LoadsCube {
  return {
    ...loadCube,
    loadsPerAxis: { x: 1, y: 1, z: 1 },
  };
}

/** Physical floors to render for one loaded layer type (one floor unless API reports several distinct Y). */
function getVisualizationFloorCount(layer: PalletLayer): number {
  const placedStacks = dedupePlacedStacksByFloorY(getPlacedLayerStacks(layer));
  if (placedStacks.length > 1) {
    return placedStacks.length;
  }
  if (placedStacks.length === 1 && (layer.layersLoaded?.length ?? 0) > 1) {
    return 1;
  }
  const boxQuantity = layer.quantity ?? 0;
  const loadedQuantity = layer.layersLoadedQuantity ?? 0;
  const sourceLoadCount = layer.loadsCubes?.length ?? 0;
  if (loadedQuantity > 1 && boxQuantity <= 1) {
    return loadedQuantity;
  }
  if (loadedQuantity > 1 && loadedQuantity !== boxQuantity) {
    return loadedQuantity;
  }
  if (loadedQuantity > 1 && sourceLoadCount <= 1) {
    return loadedQuantity;
  }
  return 1;
}

function resolveVisualizationFloorCount(
  layer: PalletLayer,
  palletGroup: PalletLayerGroup,
): number {
  const baseCount = getVisualizationFloorCount(layer);
  const sourceLoads = layer.loadsCubes ?? [];
  if (sourceLoads.length > 1) {
    const templateYs = new Set(sourceLoads.map((cube) => cube.position?.y ?? 0));
    if (templateYs.size === 1) {
      return baseCount;
    }
  }
  if (baseCount > 1 || palletGroup.loadType === 2) {
    return baseCount;
  }
  const layerHeight = layer.dimensions?.height ?? palletGroup.dimensions?.height ?? 0;
  const loadHeight = getVisualLoadStackHeight(
    palletGroup.loadType,
    layer.loadsCubes?.[0]?.dimensions,
    palletGroup.dimensions,
  );
  if (layerHeight > loadHeight + 0.01 && loadHeight > 0) {
    const tiers = Math.round(layerHeight / loadHeight);
    if (tiers > 1) {
      return tiers;
    }
  }
  return baseCount;
}

function shouldUseTemplateFloorY(
  layer: PalletLayer,
  layerLoads: LoadsCube[],
  palletGroup: PalletLayerGroup,
): boolean {
  if (resolveVisualizationFloorCount(layer, palletGroup) !== 1) {
    return false;
  }
  if ((layer.loadsCubes?.length ?? 0) <= 1) {
    return false;
  }
  return new Set(layerLoads.map((cube) => cube.position?.y ?? 0)).size === 1;
}

function getSkippedUnloadedLayerHeightsBefore(
  palletGroup: PalletLayerGroup,
  currentLayer: PalletLayer,
): number {
  let sum = 0;
  for (const layer of palletGroup.layers ?? []) {
    if (layer.id === currentLayer.id) {
      break;
    }
    if (!layerHasLoadedStacks(layer)) {
      sum += layer.dimensions?.height ?? palletGroup.dimensions?.height ?? 0;
    }
  }
  return sum;
}

function resolvePalletLayerLoadY(params: {
  useTemplateFloorY: boolean;
  templateFloorY: number;
  baseYOffset: number;
  floorOffsetY: number;
  stackBaseY: number;
  groupStackStartY: number;
  interlayerHeight: number;
  palletDeckY: number;
  palletGroup: PalletLayerGroup;
  layer: PalletLayer;
}): number {
  const gap =
    params.interlayerHeight > 0 && params.stackBaseY > params.baseYOffset
      ? VISUAL_STACK_CONTACT_GAP
      : 0;
  const computedY = params.floorOffsetY + params.stackBaseY + gap;

  if (!params.useTemplateFloorY) {
    if (
      params.baseYOffset === 0 &&
      params.palletDeckY > 0 &&
      params.stackBaseY === params.groupStackStartY &&
      computedY < params.palletDeckY - 0.01
    ) {
      return params.palletDeckY + gap;
    }
    return computedY;
  }

  if (params.stackBaseY === params.groupStackStartY) {
    if (params.baseYOffset > 0) {
      return computedY;
    }
    const templateY = params.templateFloorY + params.baseYOffset;
    if (
      params.groupStackStartY > params.baseYOffset + 0.01 &&
      templateY < params.palletDeckY + params.groupStackStartY - 0.01
    ) {
      return computedY;
    }
    if (
      params.palletDeckY > 0 &&
      templateY < params.palletDeckY - 0.01
    ) {
      return params.palletDeckY + gap;
    }
    return templateY;
  }

  const skipped = getSkippedUnloadedLayerHeightsBefore(params.palletGroup, params.layer);
  const templateAdjusted = params.templateFloorY + params.baseYOffset - skipped;
  return Math.abs(templateAdjusted - computedY) < 1 ? templateAdjusted : computedY;
}

function getVisualizationStackLayerCount(palletGroup: PalletLayerGroup): number {
  return filterLoadedLayerTypes(palletGroup.layers ?? []).reduce(
    (sum, layer) => sum + resolveVisualizationFloorCount(layer, palletGroup),
    0,
  );
}

/** Vertical extent of a load in the 3D view (horizontal pipes stack by diameter, not length). */
function getVisualLoadStackHeight(
  loadType: number | undefined,
  dimensions: Dimensions | undefined,
  fallbackDimensions?: Dimensions,
): number {
  const diameter = dimensions?.diameter ?? fallbackDimensions?.diameter;
  if (loadType === 2 && diameter != null && diameter > 0) {
    return diameter;
  }
  return dimensions?.height ?? fallbackDimensions?.height ?? 0;
}

function getAbsoluteStackTop(
  loadType: number | undefined,
  layerCubes: LoadsCube[],
  interlayerCubes: LoadsCube[],
  fallbackDimensions?: Dimensions,
): number {
  const tops = [
    ...layerCubes.map(
      (cube) =>
        (cube.position?.y ?? 0) +
        getVisualLoadStackHeight(loadType, cube.dimensions, fallbackDimensions),
    ),
    ...interlayerCubes.map(
      (cube) => (cube.position?.y ?? 0) + (cube.dimensions?.height ?? 0),
    ),
  ];
  return tops.length ? Math.max(...tops) : 0;
}

function getStackHeightForNextGroup(
  palletGroup: PalletLayerGroup,
  palletDeckY: number,
  currentY: number,
  layerCubes: LoadsCube[],
  interlayerCubes: LoadsCube[],
): number {
  const absoluteTop = getAbsoluteStackTop(
    palletGroup.loadType,
    layerCubes,
    interlayerCubes,
    palletGroup.dimensions,
  );
  const fromCubes = palletDeckY > 0 ? absoluteTop - palletDeckY : absoluteTop;
  if (currentY > fromCubes + 0.01) {
    return fromCubes;
  }
  if (palletDeckY > 0) {
    return Math.max(currentY, fromCubes);
  }
  return Math.max(currentY, fromCubes);
}

function dedupeInterlayerCubesInPlace(interlayerCubes: LoadsCube[]): void {
  const seenYs: number[] = [];
  for (let index = interlayerCubes.length - 1; index >= 0; index--) {
    const yKey = Math.round((interlayerCubes[index].position?.y ?? 0) * 100);
    if (seenYs.includes(yKey)) {
      interlayerCubes.splice(index, 1);
    } else {
      seenYs.push(yKey);
    }
  }
}

/** Align interlayer slabs and pipe loads for onBottom/onTop stacking rules. */
function reconcileInterlayerCubeYs(
  interlayerCubes: LoadsCube[],
  layerCubes: LoadsCube[],
  palletGroup: PalletLayerGroup,
  floorOffsetY: number,
  baseYOffset: number,
  palletDeckY: number,
  interlayer?: PalletInterlayer,
): void {
  if (!interlayer) {
    return;
  }

  const stackFloorY = floorOffsetY + baseYOffset;
  const deckY =
    baseYOffset === 0 && palletDeckY > 0 ? Math.max(stackFloorY, palletDeckY) : stackFloorY;

  if (baseYOffset > 0) {
    for (const cube of interlayerCubes) {
      const y = cube.position?.y ?? 0;
      if (y < stackFloorY - 0.01) {
        cube.position = { ...cube.position, y: stackFloorY };
      }
    }
  }

  if (baseYOffset === 0 && palletDeckY > 0) {
    for (const cube of interlayerCubes) {
      const y = cube.position?.y ?? 0;
      if (y < palletDeckY - 0.01) {
        cube.position = { ...cube.position, y: palletDeckY };
      }
    }
  }

  if (palletGroup.loadType !== 2 || !layerCubes.length) {
    if (layerCubes.length && interlayer?.onBottom && interlayerCubes.length) {
      const sortedInterlayerYs = [...interlayerCubes]
        .map((cube) => cube.position?.y ?? 0)
        .sort((a, b) => a - b);
      const bottomSlabY = sortedInterlayerYs[0];
      const bottomSlab = interlayerCubes.find(
        (cube) => Math.abs((cube.position?.y ?? 0) - bottomSlabY) < 0.01,
      );
      const bottomSlabTop =
        bottomSlabY + (bottomSlab?.dimensions?.height ?? interlayer.dimensions?.height ?? 0);
      const requiredLoadY =
        bottomSlabTop + (bottomSlabTop > deckY + 0.01 ? VISUAL_STACK_CONTACT_GAP : 0);
      for (const cube of layerCubes) {
        const y = cube.position?.y ?? 0;
        if (y < requiredLoadY - 0.01) {
          cube.position = { ...cube.position, y: requiredLoadY };
        }
      }
    }
    dedupeInterlayerCubesInPlace(interlayerCubes);
    return;
  }

  const sortedInterlayerYs = [...interlayerCubes]
    .map((cube) => cube.position?.y ?? 0)
    .sort((a, b) => a - b);

  if (interlayer.onBottom && interlayerCubes.length) {
    const bottomSlabY = sortedInterlayerYs[0];
    const bottomSlab = interlayerCubes.find(
      (cube) => Math.abs((cube.position?.y ?? 0) - bottomSlabY) < 0.01,
    );
    const bottomSlabTop =
      bottomSlabY + (bottomSlab?.dimensions?.height ?? interlayer.dimensions?.height ?? 0);
    const requiredPipeY =
      bottomSlabTop + (bottomSlabTop > deckY + 0.01 ? VISUAL_STACK_CONTACT_GAP : 0);

    for (const cube of layerCubes) {
      const y = cube.position?.y ?? 0;
      if (y < requiredPipeY - 0.01) {
        cube.position = { ...cube.position, y: requiredPipeY };
      }
    }
  }

  const loadedTop = getAbsoluteStackTop(
    palletGroup.loadType,
    layerCubes,
    [],
    palletGroup.dimensions,
  );
  const minPipeY = Math.min(...layerCubes.map((cube) => cube.position?.y ?? 0));

  if (interlayer.onTop) {
    const movableSlabs = interlayerCubes.filter((cube) => {
      const y = cube.position?.y ?? 0;
      const isDeckSlab = interlayer.onBottom && Math.abs(y - deckY) < 0.01;
      return !isDeckSlab;
    });
    const topSlabY = movableSlabs.length
      ? Math.max(...movableSlabs.map((cube) => cube.position?.y ?? 0))
      : loadedTop;
    for (const cube of movableSlabs) {
      const y = cube.position?.y ?? 0;
      const isTopSlab = Math.abs(y - topSlabY) < 0.01;
      const isStaleApiSlab = y < minPipeY - 0.01;
      if ((isTopSlab || isStaleApiSlab) && Math.abs(y - loadedTop) > 0.01) {
        cube.position = { ...cube.position, y: loadedTop };
      }
    }
  }

  dedupeInterlayerCubesInPlace(interlayerCubes);
}

/** Deck baseline for stacking math; API templates may already include prior stack height. */
function resolveStackFloorOffsetY(
  palletGroup: PalletLayerGroup,
  baseYOffset: number,
  palletDeckY: number,
): number {
  if (baseYOffset > 0 && palletDeckY > 0) {
    return palletDeckY;
  }
  const templateFloor = getStackFloorOffsetY(palletGroup);
  if (baseYOffset === 0 && palletDeckY > 0) {
    return Math.max(templateFloor, palletDeckY);
  }
  return templateFloor;
}

/** Expands pallet layer loads and interlayer slabs for 3D visualization. */
function expandPalletStackCubes(
  palletGroup: PalletLayerGroup,
  baseYOffset = 0,
  palletFootprint?: Dimensions,
  palletDeckY = 0,
): {
  layerCubes: LoadsCube[];
  interlayerCubes: LoadsCube[];
  interlayer?: PalletInterlayer;
  stackHeight: number;
} {
  const layerCubes: LoadsCube[] = [];
  const interlayerCubes: LoadsCube[] = [];
  const interlayerRaw = palletGroup.interlayer;
  const interlayer = getNormalizedInterlayerForStack(palletGroup, palletFootprint);
  const interlayerSlab = interlayer ? getInterlayerSlabDimensions(interlayer) : undefined;
  const interlayerHeight = interlayerSlab?.height ?? 0;
  const placedInterlayers = interlayerRaw?.loadedInterlayers ?? [];
  const usesApiInterlayerPositions = placedInterlayers.length > 0;

  if (usesApiInterlayerPositions && interlayer && interlayerSlab) {
    interlayerCubes.push(
      ...expandInterlayerCubesFromApi(interlayer, interlayerSlab, placedInterlayers),
    );
  }

  const regularity =
    interlayer?.regularity && interlayer.regularity > 0 ? Math.max(1, interlayer.regularity) : 0;
  const floorOffsetY = resolveStackFloorOffsetY(palletGroup, baseYOffset, palletDeckY);
  const totalStackLayers = getVisualizationStackLayerCount(palletGroup);
  let currentY = baseYOffset;
  let stackCount = 0;
  let placedInterlayerAfterLastLayer = false;

  if (interlayer?.onBottom && interlayerHeight > 0) {
    const bottomSlabY =
      palletDeckY > 0 && baseYOffset === 0 ? palletDeckY : floorOffsetY + currentY;
    if (
      !interlayerCubes.some((cube) => Math.abs((cube.position?.y ?? 0) - bottomSlabY) < 0.01)
    ) {
      interlayerCubes.push(
        createInterlayerLoadCube(interlayer, { x: 0, y: bottomSlabY, z: 0 }, interlayerSlab),
      );
    }
    currentY += interlayerHeight;
  }

  const groupStackStartY = currentY;
  const stackFloorOffsetY =
    groupStackStartY > baseYOffset && palletDeckY > 0 ? palletDeckY : floorOffsetY;

  const toInterlayerAbsoluteY = (relativeY: number): number => {
    if (groupStackStartY > baseYOffset && palletDeckY > 0) {
      return stackFloorOffsetY + relativeY;
    }
    return floorOffsetY + relativeY;
  };

  const hasInterlayerAt = (relativeY: number): boolean => {
    const targetY = toInterlayerAbsoluteY(relativeY);
    return interlayerCubes.some(
      (cube) => Math.abs((cube.position?.y ?? 0) - targetY) < 0.01,
    );
  };

  const placeInterlayerAt = (y: number): void => {
    if (!interlayer || !interlayerSlab || hasInterlayerAt(y)) {
      return;
    }
    interlayerCubes.push(
      createInterlayerLoadCube(
        interlayer,
        { x: 0, y: toInterlayerAbsoluteY(y), z: 0 },
        interlayerSlab,
      ),
    );
  };

  const shouldPlaceInterlayerAfterLayer = (layerStackIndex: number): boolean => {
    if (regularity <= 0 || interlayerHeight <= 0) {
      return false;
    }
    if (layerStackIndex % regularity !== 0) {
      return false;
    }
    const isLastLayer = layerStackIndex === totalStackLayers;
    return !!interlayer?.onTop || !isLastLayer;
  };

  filterLoadedLayerTypes(palletGroup.layers ?? []).forEach((layer) => {
    const layerHeight = layer.dimensions?.height ?? palletGroup.dimensions?.height ?? 0;
    const layerLoads = getLayerLoadsForVisualization(layer, palletGroup);
    const floorCount = resolveVisualizationFloorCount(layer, palletGroup);
    const visualFloorHeight =
      floorCount > 1
        ? Math.max(
            0,
            ...layerLoads.map((cube) =>
              getVisualLoadStackHeight(
                palletGroup.loadType,
                cube.dimensions,
                palletGroup.dimensions,
              ),
            ),
          )
        : layerHeight;
    const stackFloorStep =
      floorCount > 1 && visualFloorHeight > 0 && visualFloorHeight < layerHeight
        ? visualFloorHeight
        : layerHeight;

    for (let floorIndex = 0; floorIndex < floorCount; floorIndex++) {
      const stackBaseY = currentY;
      const useTemplateFloorY = shouldUseTemplateFloorY(layer, layerLoads, palletGroup);
      const templateFloorY = layerLoads[0]?.position?.y ?? 0;
      const loadY = resolvePalletLayerLoadY({
        useTemplateFloorY,
        templateFloorY,
        baseYOffset,
        floorOffsetY: stackFloorOffsetY,
        stackBaseY,
        groupStackStartY,
        interlayerHeight,
        palletDeckY,
        palletGroup,
        layer,
      });
      layerLoads.forEach((loadCube) => {
        layerCubes.push(
          normalizePalletLayerLoadCubeForVisualization(
            loadCube,
            loadY,
            loadCube.dimensions ?? palletGroup.dimensions,
          ),
        );
      });
      currentY += stackFloorStep;
      if (
        stackFloorStep < layerHeight &&
        interlayerHeight > 0 &&
        stackBaseY > baseYOffset
      ) {
        currentY += VISUAL_STACK_CONTACT_GAP;
      }
      stackCount++;
      if (shouldPlaceInterlayerAfterLayer(stackCount)) {
        placeInterlayerAt(currentY);
        currentY += interlayerHeight;
        if (stackCount === totalStackLayers) {
          placedInterlayerAfterLastLayer = true;
        }
      }
    }
  });

  if (interlayer?.onTop && interlayerHeight > 0 && !placedInterlayerAfterLastLayer) {
    placeInterlayerAt(currentY);
    currentY += interlayerHeight;
  }

  reconcileInterlayerCubeYs(
    interlayerCubes,
    layerCubes,
    palletGroup,
    floorOffsetY,
    baseYOffset,
    palletDeckY,
    interlayer,
  );

  const stackHeight = getStackHeightForNextGroup(
    palletGroup,
    palletDeckY,
    currentY,
    layerCubes,
    interlayerCubes,
  );

  return { layerCubes, interlayerCubes, interlayer, stackHeight };
}

type InterlayerApiShape = PalletInterlayer & { thickness?: number };

/**
 * Resolve interlayer thickness and footprint from API payload (supports `thickness` and partial
 * `dimensions`). When `palletFootprint` is provided it takes precedence over the box-sized
 * `interlayer.dimensions`, so the slab spans the whole physical pallet base.
 */
export function normalizePalletInterlayerForVisualization(
  interlayer: PalletInterlayer,
  palletGroup: PalletLayerGroup,
  footprint?: Dimensions,
  palletFootprint?: Dimensions,
): PalletInterlayer {
  const raw = interlayer as InterlayerApiShape;
  const palletDims = palletGroup.dimensions;
  const height = raw.dimensions?.height ?? raw.thickness ?? 0;
  const width =
    palletFootprint?.width ?? raw.dimensions?.width ?? palletDims?.width ?? footprint?.width ?? 0;
  const length =
    palletFootprint?.length ?? raw.dimensions?.length ?? palletDims?.length ?? footprint?.length ?? 0;
  return {
    ...interlayer,
    dimensions: { width, length, height },
  };
}

export function expandPalletInterlayerCubes(
  palletGroup: PalletLayerGroup,
  baseYOffset = 0,
  palletFootprint?: Dimensions,
  palletDeckY = 0,
): LoadsCube[] {
  return expandPalletStackCubes(palletGroup, baseYOffset, palletFootprint, palletDeckY)
    .interlayerCubes;
}

function findFirstLayerLoadTemplate(palletGroup: PalletLayerGroup): LoadsCube | undefined {
  for (const layer of filterLoadedLayerTypes(palletGroup.layers ?? [])) {
    const loads = getLayerLoadsForVisualization(layer, palletGroup);
    if (loads[0]) {
      return loads[0];
    }
  }
  return undefined;
}

/** Pallet-deck Y baked into API layer templates; interlayer slabs must use the same offset. */
function getStackFloorOffsetY(palletGroup: PalletLayerGroup): number {
  const templateYs = filterLoadedLayerTypes(palletGroup.layers ?? []).map(
    (layer) => layer.loadsCubes?.[0]?.position?.y ?? 0,
  );
  if (!templateYs.length) {
    return 0;
  }
  return Math.min(...templateYs);
}

function getLayerLoadsForVisualization(
  layer: PalletLayer,
  palletGroup: PalletLayerGroup,
): LoadsCube[] {
  const sourceLoads = layer.loadsCubes ?? [];
  const quantity = layer.quantity ?? sourceLoads.length;

  if (quantity <= 0 || sourceLoads.length === 0) {
    return sourceLoads.map(sanitizeLoadsPerAxisForVisualization);
  }

  const distinctPositions = new Set(
    sourceLoads.map(
      (loadCube) =>
        `${loadCube.position?.x ?? 0}_${loadCube.position?.y ?? 0}_${loadCube.position?.z ?? 0}`,
    ),
  ).size;

  if (distinctPositions >= quantity) {
    return sourceLoads
      .slice(0, quantity)
      .map(sanitizeLoadsPerAxisForVisualization);
  }

  const dimensions = sourceLoads[0].dimensions ?? palletGroup.dimensions;
  if (!canGenerateLayerGrid(dimensions)) {
    return sourceLoads.map(sanitizeLoadsPerAxisForVisualization);
  }

  return generateLayerGridLoads(layer, sourceLoads[0], dimensions, quantity);
}

function canGenerateLayerGrid(dimensions: Dimensions | undefined): boolean {
  return !!dimensions?.width && !!dimensions.length;
}

function generateLayerGridLoads(
  layer: PalletLayer,
  template: LoadsCube,
  dimensions: Dimensions,
  quantity: number,
): LoadsCube[] {
  const columns = getLayerColumnCount(layer, dimensions, quantity);
  const rows = Math.ceil(quantity / columns);
  const originX = Math.max(0, (template.position?.x ?? 0) - dimensions.width * (columns - 1));
  const originZ = Math.max(0, (template.position?.z ?? 0) - dimensions.length * (rows - 1));

  return Array.from({ length: quantity }, (_, index) => ({
    ...template,
    position: {
      ...template.position,
      x: originX + (index % columns) * dimensions.width,
      z: originZ + Math.floor(index / columns) * dimensions.length,
    },
    dimensions: template.dimensions ?? dimensions,
    loadsPerAxis: { x: 1, y: 1, z: 1 },
  }));
}

function getLayerColumnCount(
  layer: PalletLayer,
  dimensions: Dimensions,
  quantity: number,
): number {
  const columnsByWidth = layer.dimensions?.width
    ? Math.floor(layer.dimensions.width / dimensions.width)
    : Math.ceil(Math.sqrt(quantity));

  return Math.max(1, Math.min(quantity, columnsByWidth));
}

export function supportsPalletLayerLoadType(loadType: number | undefined | null): boolean {
  return loadType === 0 || loadType === 1 || loadType === 2;
}

/** True when at least one pallet layer type has been committed to the pallet (layersLoadedQuantity > 0). */
export function layerHasLoadedStacks(layer: PalletLayer): boolean {
  if (layer.layersLoadedQuantity !== undefined && layer.layersLoadedQuantity !== null) {
    return layer.layersLoadedQuantity > 0;
  }
  if (Array.isArray(layer.layersLoaded) && layer.layersLoaded.length > 0) {
    return getLoadedLayerCount(layer) > 0;
  }
  return false;
}

export function filterArrangementLayerOptions(layers: PalletLayer[]): PalletLayer[] {
  return (layers ?? []).filter((layer) => (layer.quantity ?? 0) > 0);
}

export function filterLoadedLayerTypes(layers: PalletLayer[]): PalletLayer[] {
  return (layers ?? []).filter(layerHasLoadedStacks);
}

export function cargoSpaceHasLoadedPalletLayers(cargoSpace: CargoSpace | undefined): boolean {
  return getPalletLayerGroupsFromCargoSpace(cargoSpace).some((group) =>
    (group.layers ?? []).some(layerHasLoadedStacks),
  );
}

/** True when the group is the same load as a committed pallet layer stack (color + loadType). */
export function isGroupLockedPalletLayerLoad(
  group: Group | undefined,
  project: Project | undefined | null,
): boolean {
  if (!group || !project?.workSpaces?.length) {
    return false;
  }
  const groupLoadType = group.loadType ?? 0;
  let locked = false;
  forEachProjectCargoSpace(project, (cargoSpace) => {
    if (!cargoSpaceHasLoadedPalletLayers(cargoSpace)) {
      return;
    }
    const matchesLayerLoad = getPalletLayerGroupsFromCargoSpace(cargoSpace).some(
      (palletGroup) =>
        palletGroup.color === group.color && (palletGroup.loadType ?? 0) === groupLoadType,
    );
    if (matchesLayerLoad) {
      locked = true;
    }
  });
  return locked;
}

/** True when the cargo space has pallet layer groups configured. */
export function cargoSpaceHasPalletLayers(cargoSpace: CargoSpace | undefined): boolean {
  return getPalletLayerGroupsFromCargoSpace(cargoSpace).length > 0;
}

function forEachWorkSpaceCargoSpace(
  workSpace: WorkSpace | undefined,
  callback: (cargoSpace: CargoSpace) => void,
): void {
  if (!workSpace) {
    return;
  }
  if (workSpace.stops?.length) {
    for (const stop of workSpace.stops) {
      for (const cargoSpace of stop.cargoSpaces ?? []) {
        callback(cargoSpace);
      }
    }
    return;
  }
  for (const cargoSpace of workSpace.cargoSpaces ?? []) {
    callback(cargoSpace);
  }
}

/** True when any cargo space in the workspace has pallet layer groups configured. */
export function workSpaceHasPalletLayers(workSpace: WorkSpace | undefined): boolean {
  let found = false;
  forEachWorkSpaceCargoSpace(workSpace, (cargoSpace) => {
    if (cargoSpaceHasPalletLayers(cargoSpace)) {
      found = true;
    }
  });
  return found;
}

/** True when any cargo space in the workspace has committed pallet layer stacks. */
export function workSpaceHasLoadedPalletLayers(workSpace: WorkSpace | undefined): boolean {
  let found = false;
  forEachWorkSpaceCargoSpace(workSpace, (cargoSpace) => {
    if (cargoSpaceHasLoadedPalletLayers(cargoSpace)) {
      found = true;
    }
  });
  return found;
}

/** True when the drop list target is "loads outside the space" (not cargo space or unloading). */
export function isOutsideSpaceDropElement(element: HTMLElement | undefined): boolean {
  if (!element?.dataset?.workSpaceIndex) {
    return false;
  }
  const { cargoSpaceIndex, isUnloadingSpace } = element.dataset;
  if (isUnloadingSpace === 'true' || isUnloadingSpace === '') {
    return false;
  }
  return cargoSpaceIndex === undefined || cargoSpaceIndex === '';
}

/**
 * Blocks drag-and-drop across the cargo-space / outside-space boundary when pallet layers
 * are active, except drops from outside onto a pallet that does not have loaded layers.
 */
export function isDragDropBetweenCargoSpaceAndOutsideBlocked(
  fromElement: HTMLElement | undefined,
  toElement: HTMLElement | undefined,
  fromCargoSpace: CargoSpace | undefined,
  toCargoSpace: CargoSpace | undefined,
  project: Project | undefined | null,
): boolean {
  const fromOutside = isOutsideSpaceDropElement(fromElement);
  const toOutside = isOutsideSpaceDropElement(toElement);
  const cargoToOutside = fromCargoSpace != null && toOutside;
  const outsideToCargo = fromOutside && toCargoSpace != null;
  if (!cargoToOutside && !outsideToCargo) {
    return false;
  }
  if (cargoToOutside && projectHasPalletLayers(project)) {
    return true;
  }
  if (outsideToCargo) {
    if (
      toCargoSpace?.type === LoadingSpaceType.Pallet &&
      !cargoSpaceHasLoadedPalletLayers(toCargoSpace)
    ) {
      return false;
    }
    return projectHasLoadedPalletLayers(project);
  }
  return false;
}

/**
 * Blocks cross-space drag-and-drop when either side is a pallet with loaded layer stacks.
 * Reordering within the same cargo space is left to list-level disable flags.
 */
export function isDragDropBetweenCargoSpacesBlocked(
  fromCargoSpace: CargoSpace | undefined,
  toCargoSpace: CargoSpace | undefined,
): boolean {
  const sameCargoSpace =
    fromCargoSpace != null && toCargoSpace != null && fromCargoSpace.id === toCargoSpace.id;
  if (sameCargoSpace) {
    return false;
  }
  return (
    cargoSpaceHasLoadedPalletLayers(fromCargoSpace) ||
    cargoSpaceHasLoadedPalletLayers(toCargoSpace)
  );
}

function forEachProjectCargoSpace(
  project: Project | undefined | null,
  callback: (cargoSpace: CargoSpace) => void,
): void {
  if (!project?.workSpaces?.length) {
    return;
  }
  for (const workSpace of project.workSpaces) {
    if (workSpace.stops?.length) {
      for (const stop of workSpace.stops) {
        for (const cargoSpace of stop.cargoSpaces ?? []) {
          callback(cargoSpace);
        }
      }
      continue;
    }
    for (const cargoSpace of workSpace.cargoSpaces ?? []) {
      callback(cargoSpace);
    }
  }
}

/** True when any cargo space in the project has pallet layer groups configured. */
export function projectHasPalletLayers(project: Project | undefined | null): boolean {
  let found = false;
  forEachProjectCargoSpace(project, (cargoSpace) => {
    if (cargoSpaceHasPalletLayers(cargoSpace)) {
      found = true;
    }
  });
  return found;
}

/** True when the cargo list shows a pallet layer group (quantity other than 0). */
export function projectDisplaysLayeredPallet(project: Project | undefined | null): boolean {
  let found = false;
  forEachProjectCargoSpace(project, (cargoSpace) => {
    const displaysLayer = getPalletLayerGroupsFromCargoSpace(cargoSpace).some(
      (palletGroup) => palletGroup.quantity !== 0,
    );
    if (displaysLayer) {
      found = true;
    }
  });
  return found;
}

/** True when any cargo space in the project has committed pallet layer stacks. */
export function projectHasLoadedPalletLayers(project: Project | undefined | null): boolean {
  let found = false;
  forEachProjectCargoSpace(project, (cargoSpace) => {
    if (cargoSpaceHasLoadedPalletLayers(cargoSpace)) {
      found = true;
    }
  });
  return found;
}

/** True when the project contains vehicle, container, or other non-pallet loading spaces. */
export function projectHasNonPalletLoadingSpaces(project: Project | undefined | null): boolean {
  let found = false;
  forEachProjectCargoSpace(project, (cargoSpace) => {
    if (cargoSpace?.type !== undefined && cargoSpace.type !== LoadingSpaceType.Pallet) {
      found = true;
    }
  });
  return found;
}

/**
 * Global "optimize all loads and spaces" is incompatible when pallet layers are active
 * alongside other loading space types in the same project.
 */
export function isAllOptimizationUnavailable(project: Project | undefined | null): boolean {
  return projectHasLoadedPalletLayers(project) && projectHasNonPalletLoadingSpaces(project);
}

/** Algorithm stacking options are hidden on pallet spaces with active layer stacks. */
export function shouldShowAlgorithmSettingsForCargoSpace(
  cargoSpace: CargoSpace | undefined,
): boolean {
  if (
    cargoSpace?.type === LoadingSpaceType.Pallet &&
    cargoSpaceHasLoadedPalletLayers(cargoSpace)
  ) {
    return false;
  }
  return true;
}

/** True when the cargo space has individually placed loads (not represented in pallet layer groups). */
export function cargoSpaceHasIndividualLoads(cargoSpace: CargoSpace | undefined): boolean {
  return listGroupsExcludingPalletLayers(cargoSpace).length > 0;
}

export function getPalletLayerGroupIds(cargoSpace: CargoSpace | undefined): Set<number> {
  return new Set(getPalletLayerGroupsFromCargoSpace(cargoSpace).map((group) => group.id));
}

export function getLoadedLayerCount(layer: PalletLayer): number {
  const placedStacks = dedupePlacedStacksByFloorY(getPlacedLayerStacks(layer));
  if (placedStacks.length > 0) {
    return placedStacks.reduce(
      (sum, stack) => sum + (stack.numberOfStackingLayers ?? 1),
      0,
    );
  }

  if (layer.layersLoadedQuantity !== undefined && layer.layersLoadedQuantity !== null) {
    return Math.max(0, layer.layersLoadedQuantity);
  }

  if (!Array.isArray(layer.layersLoaded)) {
    return 1;
  }

  const count = layer.layersLoaded.reduce<number>((sum, loadedLayer) => {
    const layerInfo = loadedLayer as { numberOfStackingLayers?: unknown };
    return (
      sum +
      (typeof layerInfo.numberOfStackingLayers === 'number'
        ? layerInfo.numberOfStackingLayers
        : 1)
    );
  }, 0);

  return Math.max(1, count);
}

export function normalizeCargoSpacePalletLayerGroups(cargoSpace: CargoSpace): CargoSpace {
  const groups = getPalletLayerGroupsFromCargoSpace(cargoSpace);
  if (groups.length) {
    cargoSpace.palletLayersGroups = groups;
  }
  return cargoSpace;
}

export function getActivePalletLayerGroups(
  project: Project | undefined | null,
  workSpaceIndex: number | undefined,
  stopIndex: number | undefined,
): PalletLayerGroup[] {
  const cargoSpaces = getVisualizationCargoSpaces(project, workSpaceIndex, stopIndex);
  return getPalletLayerGroupsFromCargoSpace(cargoSpaces[0]);
}

function collectArrangementLayersAcrossGroups(
  groups: PalletLayerGroup[],
  loadColor?: number,
  options?: { includeZeroQuantity?: boolean },
): PalletLayer[] {
  const matching =
    loadColor != null ? groups.filter((group) => group.color === loadColor) : [...groups];
  if (!matching.length) {
    return [];
  }
  const latest = matching[matching.length - 1];
  const others = matching
    .slice(0, -1)
    .sort((a, b) => (b.layers?.length ?? 0) - (a.layers?.length ?? 0));
  const orderedGroups = [latest, ...others];
  const seen = new Set<string>();
  const result: PalletLayer[] = [];
  for (const group of orderedGroups) {
    const fromLatest = group === latest;
    for (const layer of group.layers ?? []) {
      if (!options?.includeZeroQuantity && (layer.quantity ?? 0) <= 0) {
        continue;
      }
      const signature = layerArrangementSignature(layer);
      if (!fromLatest && seen.has(signature)) {
        continue;
      }
      seen.add(signature);
      result.push(layer);
    }
  }
  return result;
}

function applyArrangementLayerLimit(layers: PalletLayer[], limit?: number): PalletLayer[] {
  return limit != null && limit > 0 ? layers.slice(0, limit) : layers;
}

/** Layer arrangement options from the latest calculate snapshot for the current load color. */
export function getArrangementLayersFromGroups(
  groups: PalletLayerGroup[],
  loadColor?: number,
  options?: {
    includeZeroQuantity?: boolean;
    mergeAllMatchingGroups?: boolean;
    limit?: number;
  },
): PalletLayer[] {
  if (options?.mergeAllMatchingGroups) {
    return applyArrangementLayerLimit(
      collectArrangementLayersAcrossGroups(groups, loadColor, options),
      options.limit,
    );
  }
  const layers = resolveMatchingPalletLayerGroup(groups, loadColor)?.layers ?? [];
  const filtered = options?.includeZeroQuantity ? layers : filterArrangementLayerOptions(layers);
  return applyArrangementLayerLimit(filtered, options?.limit);
}

export function resolveMatchingPalletLayerGroup(
  groups: PalletLayerGroup[],
  loadColor?: number,
): PalletLayerGroup | undefined {
  if (!groups.length) {
    return undefined;
  }
  if (loadColor != null) {
    const byColor = groups.filter((group) => group.color === loadColor);
    if (byColor.length) {
      return byColor[byColor.length - 1];
    }
  }
  return groups[groups.length - 1];
}

/** Loads that fit on the most recently calculated single layer. */
export function resolveSingleLayerCapacity(
  groups: PalletLayerGroup[],
  loadColor?: number,
): number {
  const group = resolveMatchingPalletLayerGroup(groups, loadColor);
  const layers = group?.layers ?? [];
  const lastLayer = layers[layers.length - 1];
  return Math.max(0, lastLayer?.quantity ?? 0);
}

/** Total loads that fit on the pallet across all calculated layers in the group. */
export function resolveFullPalletCapacity(
  groups: PalletLayerGroup[],
  loadColor?: number,
): number {
  const group = resolveMatchingPalletLayerGroup(groups, loadColor);
  return (group?.layers ?? []).reduce((sum, layer) => sum + (layer.quantity ?? 0), 0);
}

/** Upper bound of stackable layers from pallet height and load height (cm/mm project units). */
export function estimateMaxPalletLayerCount(
  cargoSpace: CargoSpace | undefined,
  loadHeight: number,
): number {
  if (!cargoSpace || loadHeight <= 0) {
    return 1;
  }
  const palletBase = cargoSpace.extraFeatures?.palletHeight ?? cargoSpace.position?.y ?? 0;
  const maxHeight =
    cargoSpace.extraFeatures?.palletMaxHeight ?? cargoSpace.dimensions?.height ?? 0;
  const available = maxHeight - palletBase;
  if (available <= 0) {
    return 1;
  }
  return Math.max(1, Math.floor(available / loadHeight));
}

export function getAllProjectCargoSpaces(project: Project | undefined | null): CargoSpace[] {
  if (!project?.workSpaces?.length) {
    return [];
  }

  const cargoSpaces: CargoSpace[] = [];
  const seenIds = new Set<number>();
  const add = (spaces?: CargoSpace[]): void => {
    for (const cargoSpace of spaces ?? []) {
      if (cargoSpace == null) {
        continue;
      }
      if (cargoSpace.id != null) {
        if (seenIds.has(cargoSpace.id)) {
          continue;
        }
        seenIds.add(cargoSpace.id);
      }
      cargoSpaces.push(cargoSpace);
    }
  };

  for (const workSpace of project.workSpaces) {
    add(workSpace.cargoSpaces);
    for (const stop of workSpace.stops ?? []) {
      add(stop.cargoSpaces);
    }
  }
  return cargoSpaces;
}

/** LDM is not shown in the per-space summary bar for pallet loading spaces. */
export function shouldShowLdmInCargoSpaceSummary(
  cargoSpace: CargoSpace | undefined | null,
): boolean {
  return cargoSpace?.type !== LoadingSpaceType.Pallet;
}

/**
 * Hide project-level LDM when the project contains only pallets.
 * Keep it when pallets are mixed with vehicles, containers, or other space types.
 */
export function shouldShowLdmInProjectSummary(project: Project | undefined | null): boolean {
  const cargoSpaces = getAllProjectCargoSpaces(project);
  if (!cargoSpaces.length) {
    return true;
  }

  return cargoSpaces.some((cargoSpace) => cargoSpace.type !== LoadingSpaceType.Pallet);
}

/** Y offset of the pallet deck used in API layer templates (cm/mm project units). */
export function getPalletDeckOffsetY(cargoSpace: CargoSpace | undefined): number {
  return cargoSpace?.extraFeatures?.palletHeight ?? cargoSpace?.position?.y ?? 0;
}

function createPreviewLayerForStack(
  layer: PalletLayer,
  layersToAdd: number,
  palletGroup: PalletLayerGroup,
  deckOffsetY: number,
): PalletLayer {
  const dimensions = layer.dimensions ?? palletGroup.dimensions;
  const loadsCubes =
    layer.loadsCubes?.length ?
      layer.loadsCubes
    : dimensions ?
      [
        {
          position: { x: 0, y: deckOffsetY, z: 0 },
          dimensions,
          loadsPerAxis: { x: 1, y: 1, z: 1 },
          state: { rotated: false, flipped: false, dragged: false },
        },
      ]
    : [];

  return {
    ...layer,
    loadsCubes,
    layersLoadedQuantity: layersToAdd,
  };
}

/** 3D groups for add-load arrangement preview: only the pending layer (no committed cargo). */
export function buildPalletLayerAddPreviewGroups(params: {
  cargoSpace: CargoSpace | undefined;
  selectedLayer: PalletLayer;
  layersToAdd: number;
  color: number;
  loadType: number;
  name?: string;
  weight?: number;
  dimensions?: Dimensions;
  interlayer?: PalletInterlayer | null;
  stackingOptions?: PalletLayerGroup['stackingOptions'];
}): Group[] {
  const cargoSpace = params.cargoSpace;
  const palletFootprint = getPalletFootprint(cargoSpace);
  const deckOffsetY = getPalletDeckOffsetY(cargoSpace);

  const pendingGroup: PalletLayerGroup = {
    id: -1,
    color: params.color,
    loadType: params.loadType,
    name: params.name,
    weight: params.weight ?? 0,
    dimensions: params.dimensions ?? params.selectedLayer.dimensions ?? palletFootprint,
    layers: [
      createPreviewLayerForStack(
        params.selectedLayer,
        Math.max(1, params.layersToAdd),
        {
          id: -1,
          color: params.color,
          loadType: params.loadType,
          dimensions: params.dimensions,
        },
        deckOffsetY,
      ),
    ],
    interlayer: params.interlayer ?? null,
    stackingOptions: params.stackingOptions,
  };

  const pendingGroups = palletLayerGroupToVisualizationGroups(
    pendingGroup,
    0,
    palletFootprint,
    deckOffsetY,
    cargoSpace,
  ).groups;

  return pendingGroups.filter((group) => group.loadsCubes.length > 0);
}
